'use client';

import CustomLink from 'components/shared/CustomLink';
import { useSearchParams } from 'next/navigation';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { useEffect, useMemo, useState } from 'react';
import {
  getWorkAffiliateDiscounts,
  PartnerElementData,
} from 'utils/affiliateHelpers';
import { Container } from '../..';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import { ButtonV2 } from 'components/shared';
import styles from './ProductCardSection.module.css';

export const ProductCardSection = (
  props: {
    className?: string;
    isEmailValid: boolean;
    isWorkAffiliate?: boolean;
    emailLinkAuthActive?: boolean;
    ultraworkSessionToken?: string | null;
    onSessionExpired?: () => void;
  } & PartnerElementData,
) => {
    const { region: country } = useRegionLocale();
    const searchParams = useSearchParams();
    const routerQuery = useMemo(() => {
      const query: Record<string, string | string[]> = {};
      if (!searchParams) return query;
      for (const key of searchParams.keys()) {
        const values = searchParams.getAll(key);
        query[key] = values.length > 1 ? values : values[0];
      }
      return query;
    }, [searchParams]);
    const { isWorkAffiliate, isEmailValid, emailLinkAuthActive } = props;

    const initialDiscountState = {
      m1Title: null,
      ringTitle: null,
      m1Code: null,
      ringCode: null,
      bloodVisionCode: null,
      bloodVisionTitle: null,
      homeCode: null,
      homeTitle: null,
    };

    const [discount, setDiscount] = useState<{
      m1Title: string | null;
      ringTitle: string | null;
      m1Code: string | null;
      ringCode: string | null;
      bloodVisionCode: string | null;
      bloodVisionTitle: string | null;
      homeCode: string | null;
      homeTitle: string | null;
    }>(initialDiscountState);

    useEffect(() => {
      if (!props.isEmailValid && !emailLinkAuthActive) {
        setDiscount(initialDiscountState);
        return;
      }

      // Discount codes now require a verified session (ULT-257 / ULT-260).
      // The magic-link path (emailLinkAuthActive) carries a sessionToken;
      // the Google-auth path (isEmailValid) does not mint one yet, so it
      // falls back to showing no discount rather than crashing.
      if (!props.ultraworkSessionToken) {
        setDiscount(initialDiscountState);
        return;
      }

      const setData = async () => {
        const result = await getWorkAffiliateDiscounts(
          props.code,
          props.ultraworkSessionToken as string,
        );

        if (result.status === 'session_invalid') {
          setDiscount(initialDiscountState);
          props.onSessionExpired?.();
          return;
        }

        if (result.status !== 'ok') {
          setDiscount(initialDiscountState);
          return;
        }

        const discounts = result.discounts;

        setDiscount({
          m1Title: discounts.m1_discount
            ? `Get ${discounts.m1_discount}% off`
            : null,
          ringTitle: discounts.ring_discount
            ? `Get ${discounts.ring_discount}% off`
            : null,
          m1Code: discounts.m1_discount_code,
          ringCode: discounts.ring_discount_code,
          // bloodVisionCode: 'fcoup_01k4sbfc89nw0zz57xag4gv8zp',
          bloodVisionCode: 'fcoup_01k5bn4hptg5kvw3tehvy85mr1',
          bloodVisionTitle: 'Get 15% off',
          homeCode: 'CP8JKNKGDY2F',
          homeTitle: 'Get 15% off',
        });
      };

      setData();
    }, [
      props.isEmailValid,
      props.emailLinkAuthActive,
      props.ultraworkSessionToken,
    ]);

    const m1LinkCard = (
      <CustomLink
        href={{
          pathname: '/pricing',
          query: {
            ...routerQuery,
            affiliateCode: props.affiliate_code,
            affiliate: props.affiliate_code,
            discount: discount?.m1Code ?? null,
            workAffiliate:
              (isWorkAffiliate && (isEmailValid || emailLinkAuthActive)) ??
              false,
          },
        }}
        className={`${styles.bentoCard} ${styles.light} ${styles.m1}`}
        style={{
          backgroundImage: `url(${getCompressedAssetUrl(
            '/web_v2/backgrounds/m1-bento-bg.png/w_1080',
          )})`,
          backgroundRepeat: 'no-repeat',
        }}
      >
        {discount?.m1Title ? (
          <span className={styles.popularTag}>{discount.m1Title}</span>
        ) : null}
        <h2>Ultrahuman M1{country == 'AE' ? ' Live' : null}</h2>
        <p>
          Supercharge your nutrition and exercise performance using glucose
          biomarkers.
        </p>
        <ButtonV2 variant="black">Buy now</ButtonV2>
      </CustomLink>
    );

    const ringLinkCard = (
      <CustomLink
        href={{
          pathname: `/ring/buy/`,
          query: {
            ...routerQuery,
            affiliateCode: props.affiliate_code,
            affiliate: props.affiliate_code,
            discount: discount?.ringCode ?? null,
            workAffiliate:
              (isWorkAffiliate && (isEmailValid || emailLinkAuthActive)) ??
              false,
          },
        }}
        className={`${styles.bentoCard} ${styles.light} ${styles.ring}`}
        style={{
          backgroundImage: `url(${getCompressedAssetUrl(
            '/web_v2/backgrounds/ring-bento-2.png/w_1080',
          )})`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'bottom',
          overflow: 'hidden',
        }}
      >
        {discount?.ringTitle ? (
          <span className={styles.popularTag}>{discount.ringTitle}</span>
        ) : null}
        <h2>Ultrahuman Ring AIR</h2>
        <p>
          The world&apos;s most comfortable and compact smart ring that monitors
          your sleep, movement and recovery.
        </p>
        <ButtonV2 variant="black">Buy now</ButtonV2>
      </CustomLink>
    );

    const bloodVisionCard =
      country === 'US' ? (
        <CustomLink
          href={{
            pathname: `/blood-vision/buy`,
            query: {
              ...routerQuery,
              affiliateCode: props.affiliate_code,
              affiliate: props.affiliate_code,
              discount: discount?.bloodVisionCode ?? null,
              workAffiliate:
                (isWorkAffiliate && (isEmailValid || emailLinkAuthActive)) ??
                false,
            },
          }}
          className={`${styles.bentoCard} ${styles.light} ${styles.m1}`}
          style={{
            backgroundImage: `url(${getCompressedAssetUrl(
              '/web_v2/backgrounds/bv_bento_affiliate.png/w_1080',
            )})`,
            backgroundPosition: 'bottom',
            overflow: 'hidden',
          }}
        >
          {discount?.bloodVisionTitle ? (
            <span className={styles.popularTag}>{discount.bloodVisionTitle}</span>
          ) : null}
          <h2>Ultrahuman Blood Vision</h2>
          <p>
            A comprehensive blood test to understand your blood markers in the
            context of lifestyle and longevity.
          </p>
          <ButtonV2 variant="black">Buy now</ButtonV2>
        </CustomLink>
      ) : null;

    const homeCard = (
      <CustomLink
        href={{
          pathname: '/home/buy',
          query: {
            ...routerQuery,
            affiliateCode: props.affiliate_code,
            affiliate: props.affiliate_code,
            discount: discount?.homeCode ?? null,
            workAffiliate:
              (isWorkAffiliate && (isEmailValid || emailLinkAuthActive)) ??
              false,
          },
        }}
        className={`${styles.bentoCard} ${styles.light} ${styles.m1}`}
        style={{
          backgroundImage: `url(${getCompressedAssetUrl(
            '/web_v2/backgrounds/home_invite_bento.png/w_1080',
          )})`,
        }}
      >
        {discount?.homeTitle ? (
          <span className={styles.popularTag}>{discount.homeTitle}</span>
        ) : null}
        <h2>Ultrahuman Home</h2>
        <p>A quiet yet powerful sleep monitor to help you rest better.</p>
        <ButtonV2 variant="black">Buy now</ButtonV2>
      </CustomLink>
    );

    return (
      <section
        className={
          props.className ? `${styles.root} ${props.className}` : styles.root
        }
      >
        <h2 className={styles.corporateHead}>
          Enjoy exclusive benefits,
          <br />
          curated for you
        </h2>
        <Container>
          {props.ring_active === true ? (
            <>
              {ringLinkCard}
              {bloodVisionCard}
              {homeCard}
            </>
          ) : null}

          {props.m1_active === true && m1LinkCard}

          {props.m1_active === undefined && props.ring_active === undefined && (
            <>
              {ringLinkCard}
              {m1LinkCard}
            </>
          )}
        </Container>
      </section>
    );
};
